<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { MinigolfLabels } from './types'

interface Vec { x: number, y: number }
interface Rect { x: number, y: number, w: number, h: number }

const props = withDefaults(defineProps<{
    labels: MinigolfLabels
    maxWidth?: number
}>(), {
    maxWidth: 760
})

const emit = defineEmits<{
    holed: [strokes: number]
}>()

// Virtual field coordinates — all game logic runs in these units, the canvas
// backing store is scaled to match device pixels on resize.
const FIELD_W = 800
const FIELD_H = 520
const WALL = 16
const BALL_R = 10
const HOLE_R = 19
const REST = 0.72
const FRICTION = 0.974
const STOP_SPEED = 12
const CAPTURE_SPEED = 360
const GRAB_R = 72
const MIN_PULL = 10
const MAX_PULL = 230
const MAX_SPEED = 1000

const wrapperEl = ref<HTMLDivElement | null>(null)
const canvasEl = ref<HTMLCanvasElement | null>(null)

const strokes = ref(0)
const holeNumber = ref(1)
const holed = ref(false)

const ball = { x: 0, y: 0, vx: 0, vy: 0 }
const tee: Vec = { x: 0, y: 0 }
const hole: Vec = { x: 0, y: 0 }
let obstacles: Rect[] = []

let dragging = false
const dragPos: Vec = { x: 0, y: 0 }

let ctx: CanvasRenderingContext2D | null = null
let rafId = 0
let lastTs = 0
let resizeObserver: ResizeObserver | null = null

const statusText = computed(() =>
    holed.value
        ? props.labels.holedStatus(strokes.value)
        : props.labels.status(holeNumber.value, strokes.value)
)

const rand = (min: number, max: number) => min + Math.random() * (max - min)
const randInt = (min: number, max: number) => Math.floor(rand(min, max + 1))
const len = (x: number, y: number) => Math.hypot(x, y)
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

function pointRectDist(px: number, py: number, r: Rect) {
    const cx = clamp(px, r.x, r.x + r.w)
    const cy = clamp(py, r.y, r.y + r.h)
    return len(px - cx, py - cy)
}

function rectsOverlap(a: Rect, b: Rect, pad: number) {
    return a.x - pad < b.x + b.w && a.x + a.w + pad > b.x
        && a.y - pad < b.y + b.h && a.y + a.h + pad > b.y
}

function generateHole() {
    tee.x = rand(WALL + 46, FIELD_W * 0.30)
    tee.y = rand(WALL + 46, FIELD_H - WALL - 46)
    hole.x = rand(FIELD_W * 0.68, FIELD_W - WALL - 46)
    hole.y = rand(WALL + 56, FIELD_H - WALL - 46)

    obstacles = []
    const target = randInt(1, 3)
    let attempts = 0
    while (obstacles.length < target && attempts < 60) {
        attempts++
        const w = rand(50, 150)
        const h = rand(24, 150)
        const rect: Rect = {
            x: rand(WALL + 24, FIELD_W - WALL - 24 - w),
            y: rand(WALL + 24, FIELD_H - WALL - 24 - h),
            w,
            h
        }
        // Keep clearance around tee and hole, and don't stack obstacles, so the
        // layout stays reachable (bank shots do the rest).
        if (pointRectDist(tee.x, tee.y, rect) < 80) continue
        if (pointRectDist(hole.x, hole.y, rect) < 70) continue
        if (obstacles.some(o => rectsOverlap(o, rect, 30))) continue
        obstacles.push(rect)
    }

    ball.x = tee.x
    ball.y = tee.y
    ball.vx = 0
    ball.vy = 0
}

function moving() {
    return len(ball.vx, ball.vy) > 0.001
}

function collideWalls() {
    const lo = WALL + BALL_R
    const hiX = FIELD_W - WALL - BALL_R
    const hiY = FIELD_H - WALL - BALL_R
    if (ball.x < lo) {
        ball.x = lo
        ball.vx = -ball.vx * REST
    } else if (ball.x > hiX) {
        ball.x = hiX
        ball.vx = -ball.vx * REST
    }
    if (ball.y < lo) {
        ball.y = lo
        ball.vy = -ball.vy * REST
    } else if (ball.y > hiY) {
        ball.y = hiY
        ball.vy = -ball.vy * REST
    }
}

function collideObstacle(r: Rect) {
    const cx = clamp(ball.x, r.x, r.x + r.w)
    const cy = clamp(ball.y, r.y, r.y + r.h)
    let nx = ball.x - cx
    let ny = ball.y - cy
    let d = len(nx, ny)

    if (d > BALL_R) return

    if (d === 0) {
        // Center is inside the rect — push out along the shallowest side.
        const left = ball.x - r.x
        const right = r.x + r.w - ball.x
        const top = ball.y - r.y
        const bottom = r.y + r.h - ball.y
        const min = Math.min(left, right, top, bottom)
        ny = 0
        nx = 0
        if (min === left) nx = -1
        else if (min === right) nx = 1
        else if (min === top) ny = -1
        else ny = 1
        d = 0.0001
    } else {
        nx /= d
        ny /= d
    }

    const overlap = BALL_R - d
    ball.x += nx * overlap
    ball.y += ny * overlap
    const vdotn = ball.vx * nx + ball.vy * ny
    ball.vx -= (1 + REST) * vdotn * nx
    ball.vy -= (1 + REST) * vdotn * ny
}

function step(dt: number) {
    if (!moving()) return

    const speed = len(ball.vx, ball.vy)
    // Sub-step so a fast ball never tunnels through a wall or obstacle.
    const substeps = Math.max(1, Math.ceil((speed * dt) / (BALL_R * 0.5)))
    const h = dt / substeps

    for (let i = 0; i < substeps; i++) {
        ball.x += ball.vx * h
        ball.y += ball.vy * h
        collideWalls()
        for (const o of obstacles) collideObstacle(o)

        if (!holed.value && len(ball.x - hole.x, ball.y - hole.y) < HOLE_R
            && len(ball.vx, ball.vy) < CAPTURE_SPEED) {
            ball.x = hole.x
            ball.y = hole.y
            ball.vx = 0
            ball.vy = 0
            holed.value = true
            emit('holed', strokes.value)
            return
        }
    }

    const decay = Math.pow(FRICTION, dt * 60)
    ball.vx *= decay
    ball.vy *= decay
    if (len(ball.vx, ball.vy) < STOP_SPEED) {
        ball.vx = 0
        ball.vy = 0
    }
}

function roundRect(x: number, y: number, w: number, h: number, r: number) {
    ctx!.beginPath()
    ctx!.roundRect(x, y, w, h, r)
}

function draw() {
    const canvas = canvasEl.value
    if (!canvas || !ctx) return
    const sx = canvas.width / FIELD_W
    const sy = canvas.height / FIELD_H
    ctx.setTransform(sx, 0, 0, sy, 0, 0)
    ctx.clearRect(0, 0, FIELD_W, FIELD_H)

    // Grass + wall border
    ctx.fillStyle = '#3ea75a'
    roundRect(0, 0, FIELD_W, FIELD_H, 22)
    ctx.fill()
    ctx.fillStyle = '#2c6b3d'
    roundRect(0, 0, FIELD_W, FIELD_H, 22)
    ctx.roundRect(WALL, WALL, FIELD_W - 2 * WALL, FIELD_H - 2 * WALL, 12)
    ctx.fill('evenodd')

    // Subtle mow stripes
    ctx.fillStyle = 'rgba(255,255,255,0.04)'
    for (let i = WALL; i < FIELD_W - WALL; i += 96) {
        ctx.fillRect(i, WALL, 48, FIELD_H - 2 * WALL)
    }

    // Obstacles
    for (const o of obstacles) {
        ctx.fillStyle = '#a86b3c'
        roundRect(o.x, o.y, o.w, o.h, 8)
        ctx.fill()
        ctx.fillStyle = 'rgba(0,0,0,0.18)'
        roundRect(o.x, o.y + o.h - 8, o.w, 8, 8)
        ctx.fill()
    }

    // Hole + flag
    ctx.fillStyle = '#15181c'
    ctx.beginPath()
    ctx.ellipse(hole.x, hole.y, HOLE_R, HOLE_R * 0.72, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = '#2c6b3d'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(hole.x, hole.y)
    ctx.lineTo(hole.x, hole.y - 52)
    ctx.stroke()
    ctx.fillStyle = '#e2453c'
    ctx.beginPath()
    ctx.moveTo(hole.x, hole.y - 52)
    ctx.lineTo(hole.x + 30, hole.y - 44)
    ctx.lineTo(hole.x, hole.y - 36)
    ctx.closePath()
    ctx.fill()

    // Grab hint ring when idle
    if (!moving() && !holed.value && !dragging) {
        ctx.strokeStyle = 'rgba(255,255,255,0.45)'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 6])
        ctx.beginPath()
        ctx.arc(ball.x, ball.y, BALL_R + 10, 0, Math.PI * 2)
        ctx.stroke()
        ctx.setLineDash([])
    }

    // Ball
    ctx.fillStyle = 'rgba(0,0,0,0.22)'
    ctx.beginPath()
    ctx.ellipse(ball.x, ball.y + BALL_R * 0.7, BALL_R, BALL_R * 0.5, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#ffffff'
    ctx.strokeStyle = '#c8ccd0'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.arc(ball.x, ball.y, BALL_R, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()

    if (dragging) drawAim()
}

function drawAim() {
    const pull = { x: ball.x - dragPos.x, y: ball.y - dragPos.y }
    const d = len(pull.x, pull.y)
    if (d < 1) return
    const power = Math.min(d, MAX_PULL) / MAX_PULL
    const dirX = pull.x / d
    const dirY = pull.y / d

    // Aim line points in shot direction (opposite the pull).
    ctx!.strokeStyle = `rgb(${Math.round(60 + 190 * power)},${Math.round(200 - 150 * power)},70)`
    ctx!.lineWidth = 4
    ctx!.setLineDash([10, 8])
    ctx!.beginPath()
    ctx!.moveTo(ball.x, ball.y)
    ctx!.lineTo(ball.x + dirX * power * 240, ball.y + dirY * power * 240)
    ctx!.stroke()
    ctx!.setLineDash([])

    // Pull marker
    ctx!.fillStyle = 'rgba(255,255,255,0.5)'
    ctx!.beginPath()
    ctx!.arc(dragPos.x, dragPos.y, 6, 0, Math.PI * 2)
    ctx!.fill()
}

function loop(ts: number) {
    const dt = Math.min((ts - lastTs) / 1000 || 0, 1 / 30)
    lastTs = ts
    step(dt)
    draw()
    rafId = requestAnimationFrame(loop)
}

function toField(e: PointerEvent): Vec {
    const rect = canvasEl.value!.getBoundingClientRect()
    return {
        x: (e.clientX - rect.left) / rect.width * FIELD_W,
        y: (e.clientY - rect.top) / rect.height * FIELD_H
    }
}

function onPointerDown(e: PointerEvent) {
    if (holed.value || moving()) return
    const p = toField(e)
    if (len(p.x - ball.x, p.y - ball.y) > GRAB_R) return
    dragging = true
    dragPos.x = p.x
    dragPos.y = p.y
    canvasEl.value!.setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
    if (!dragging) return
    const p = toField(e)
    dragPos.x = p.x
    dragPos.y = p.y
}

function onPointerUp(e: PointerEvent) {
    if (!dragging) return
    dragging = false
    canvasEl.value?.releasePointerCapture(e.pointerId)
    const pull = { x: ball.x - dragPos.x, y: ball.y - dragPos.y }
    const d = len(pull.x, pull.y)
    if (d < MIN_PULL) return
    const speed = Math.min(d, MAX_PULL) / MAX_PULL * MAX_SPEED
    ball.vx = pull.x / d * speed
    ball.vy = pull.y / d * speed
    strokes.value++
}

function onPointerCancel() {
    dragging = false
}

function nextHole() {
    holeNumber.value++
    strokes.value = 0
    holed.value = false
    generateHole()
}

function newHole() {
    strokes.value = 0
    holed.value = false
    generateHole()
}

function resizeCanvas(entry?: ResizeObserverEntry) {
    const canvas = canvasEl.value
    if (!canvas) return
    const dpc = entry?.devicePixelContentBoxSize
    if (dpc && dpc[0]) {
        canvas.width = dpc[0].inlineSize
        canvas.height = dpc[0].blockSize
    } else {
        const w = canvas.clientWidth || 1
        canvas.width = Math.round(w * window.devicePixelRatio)
        canvas.height = Math.round(w * (FIELD_H / FIELD_W) * window.devicePixelRatio)
    }
}

onMounted(() => {
    const canvas = canvasEl.value
    if (!canvas) return
    ctx = canvas.getContext('2d')
    generateHole()
    resizeCanvas()

    const supportsDpcb = typeof ResizeObserverEntry !== 'undefined'
        && 'devicePixelContentBoxSize' in ResizeObserverEntry.prototype
    resizeObserver = new ResizeObserver(([entry]) => resizeCanvas(entry))
    resizeObserver.observe(canvas, supportsDpcb ? { box: 'device-pixel-content-box' } : {})

    rafId = requestAnimationFrame(loop)
})

onBeforeUnmount(() => {
    cancelAnimationFrame(rafId)
    resizeObserver?.disconnect()
})
</script>

<template>
    <div
        ref="wrapperEl"
        class="relative mx-auto w-full select-none"
        :style="{ maxWidth: `${props.maxWidth}px` }"
    >
        <canvas
            ref="canvasEl"
            class="block aspect-[800/520] w-full touch-none rounded-2xl shadow-lg"
            role="img"
            :aria-label="labels.canvasAria"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerCancel"
        />

        <div
            class="absolute left-3 top-3 rounded-full bg-black/55 px-4 py-1.5 text-sm font-semibold text-white"
            aria-live="polite"
        >
            {{ statusText }}
        </div>

        <button
            type="button"
            class="btn btn-sm absolute right-3 top-3"
            @click="newHole"
        >
            {{ labels.newHole }}
        </button>

        <div
            v-if="holed"
            class="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/45"
        >
            <div class="card bg-base-100 text-center shadow-xl">
                <div class="card-body items-center gap-3">
                    <div class="text-3xl">
                        ⛳️🎉
                    </div>
                    <h3 class="text-xl font-bold">
                        {{ labels.holedTitle }}
                    </h3>
                    <p class="text-base-content/70">
                        {{ labels.holedStrokes(strokes) }}
                    </p>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="nextHole"
                    >
                        {{ labels.nextHole }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
